// INTEL CONFIDENTIAL
// Copyright (C) 2023 Intel Corporation

import React, { useEffect, useState } from 'react'
import './HeaderAI.scss'
import { Navbar, Nav, ButtonGroup, Button, Dropdown } from 'react-bootstrap'
import { BsList } from 'react-icons/bs'

interface HeaderItem {
  url: string
  title: string
  openInNewTab?: boolean
}

interface HeaderItemLinkProps {
  item: HeaderItem
  isActive: boolean
  isDropdownOption?: boolean
}

const HeaderItemLink: React.FC<HeaderItemLinkProps> = ({ item, isActive, isDropdownOption }): JSX.Element => {
  return (
    <a
      intc-id={`link-nav-bar-expand-${item.title}`}
      data-wap_ref={`link-nav-bar-expand-${item.title}`}
      href={item.url}
      key={item.title}
      role="link"
      className={`btn btn-outline-secondary border-0 shadow-none ${isActive ? 'activeOptionColor' : ''} ${isDropdownOption ? 'w-100 justify-content-start' : ''}`}
      target={item?.openInNewTab ? '_blank' : '_self'}
      rel={item?.openInNewTab ? 'noreferrer' : undefined}
    >
      {item.title}
    </a>
  )
}

interface HeaderAIProps {
  alwaysDisplayWhiteBg?: boolean
  toggleMenuVisibility?: () => void
}

const HeaderAI: React.FC<HeaderAIProps> = ({ alwaysDisplayWhiteBg = false, toggleMenuVisibility }) => {
  const siteURL: any = process.env.CONSOLE_URL
  const docsURL: any = process.env.DOCUMENTATION_URL
  const contactSalesURL: any = process.env.CONTACT_SALES_URL

  const options = [
    { title: 'Home', url: '/' },
    { title: 'Products', url: '/products' },
    {
      title: 'Solutions',
      children: [
        { title: 'Enterprise', url: '/solutions/enterprise' },
        {
          title: 'Developers',
          url: '/solutions/developers'
        },
        {
          title: 'Startups',
          url: '/solutions/startups'
        }
      ]
    },
    { title: 'Support', url: '/support' },
    { title: 'Docs', url: docsURL, openInNewTab: true }
  ]

  const [isActive, setIsActive] = useState(alwaysDisplayWhiteBg)

  useEffect(() => {
    const scrollContainer: any = document.querySelector('#___gatsby') ?? window
    const handleScroll = (): void => {
      if (scrollContainer?.scrollTop > 70) {
        setIsActive(true)
      } else {
        setIsActive(!!alwaysDisplayWhiteBg)
      }
    }

    scrollContainer?.addEventListener('scroll', handleScroll)

    return () => {
      scrollContainer?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const scrollToTop = (): void => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  const getItemChildren = (firstLevel: any): any[] => {
    const items: any[] = []
    if (firstLevel.children) {
      firstLevel.children.forEach((secondLevel: any, index: number) => {
        items.push(<HeaderItemLink item={secondLevel} key={index} isActive={isActive} isDropdownOption={true} />)
      })
    }
    return items
  }

  const listItems: any = []
  options.forEach((firstLevel) => {
    if (firstLevel.children === undefined && firstLevel.url) {
      listItems.push(<HeaderItemLink item={firstLevel} isActive={isActive} />)
    }
    if (firstLevel.children && !firstLevel.url) {
      const menu = (
        <Dropdown>
          <Dropdown.Toggle
            intc-id={`link-nav-bar-expand-${firstLevel.title}`}
            className={`btn btn-outline-secondary border-0 shadow-none ${isActive ? 'activeOptionColor' : ''}`}
            id="dropdown-basic"
          >
            {firstLevel.title}
          </Dropdown.Toggle>
          <Dropdown.Menu className={`border-0 ${isActive ? '' : 'inactiveDropdownBg'}`}>
            {getItemChildren(firstLevel)}
          </Dropdown.Menu>
        </Dropdown>
      )
      listItems.push(menu)
    }
  })

  return (
    <div
      className={`w-100 z-1 header-container ${isActive ? 'position-fixed slideUp' : 'position-absolute slideDown'}`}
    >
      <Navbar
        className={`siteNavbar border-bottom-0 flex-fill ${!isActive ? 'bg-transparent ' : ''}`}
        expand="lg"
        variant="dark"
        aria-label={process.env.LANDING_SHORT_NAME}
      >
        <div className="d-flex flex-row gap-s6 py-s3 my-s1 order-0">
          <Button
            intc-id="btn-site-nav-toggle"
            data-wap_ref="btn-site-nav-toggle"
            className={`border p-0 btn btn-icon-simple ${isActive ? 'border-primary' : 'border-secondary'} d-lg-none`}
            onClick={toggleMenuVisibility}
          >
            <BsList className={isActive ? 'text-primary' : 'text-secondary'} />
          </Button>
          <Navbar.Brand intc-id="site-nav-brand" className="p-s0">
            <div
              className="d-flex align-items-center gap-s5 p-s0"
              intc-id="site-header-button"
              data-wap_ref="site-header-button"
              aria-label="Go to top of the page"
              rel="noreferrer"
              onClick={scrollToTop}
            >
              <img src={'/images/header-icon.svg'} alt="tiber-icon" className="d-block img-fluid" />
              <img
                src={isActive ? '/images/wordmark-dark.svg' : '/images/wordmark-light.svg'}
                alt="tiber-icon"
                className="d-block img-fluid"
              />
            </div>
          </Navbar.Brand>
        </div>
        <ButtonGroup className="d-none d-lg-flex gap-s5">
          {listItems.map((item: JSX.Element, index: number) => (
            <div key={index}>{item}</div>
          ))}
        </ButtonGroup>
        <ButtonGroup className="side-section-right">
          <Nav.Link
            className={`btn ${isActive ? 'btn-outline-primary' : 'btn-outline-secondary'} btn-sm buttonWidth90 d-none d-sm-inline-flex `}
            href={contactSalesURL}
            role="button"
            intc-id="btn-get-started"
            data-wap_ref="btn-get-started"
            rel="noreferrer"
          >
            Contact
          </Nav.Link>
          <Nav.Link
            className={`btn ${isActive ? 'btn-primary' : 'btn-secondary'} btn-sm buttonWidth90 d-none d-sm-flex`}
            href={siteURL}
            role="button"
            intc-id="btn-get-started"
            data-wap_ref="btn-get-started"
            rel="noreferrer"
          >
            Sign up
          </Nav.Link>
          <Nav.Link
            className={`btn ${
              isActive ? 'btn-outline-primary' : 'btn-outline-secondary'
            } btn-sm buttonWidth90 d-flex d-sm-none`}
            href={siteURL}
            role="button"
            intc-id="btn-get-started"
            data-wap_ref="btn-get-started"
            rel="noreferrer"
          >
            Sign up
          </Nav.Link>
        </ButtonGroup>
      </Navbar>
    </div>
  )
}

export default HeaderAI
